import { useContext, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  createFilterOptions,
  FormGroup,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CorePropsInterfaces, CoreInterfaces, DTOs } from "src/core/Models";
import * as Constants from "../../../core/Constants";
import { t } from "i18next";
import GtDialog from "src/components/Common/GtDialog";
import { AppContext } from "src/contexts/AppContext";
import {
  createEmptyContactPerson,
  formatStringToPersonalNumber,
  isFinalorOutdatedVersion,
} from "src/utils";

import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import GtDebouncedInput from "src/components/Common/GtDebouncedInput";

// Match on the person's name only, not the "<email>" decoration.
const contactFilterOptions =
  createFilterOptions<CoreInterfaces.SelectableKeyValueItem>({
    stringify: (option) => (option.value ?? "").replace(/<[^>]*>/g, ""),
  });

function QuestionContactPersonUserDetails({
  question,
  isDisabled,
}: CorePropsInterfaces.QuestionInputProps) {
  const { globalState, dispatch } = useContext(AppContext);
  const lastVersionChangedDate =
    globalState.currentConfiguration.lastModifiedDate;
  const isFinalOrOutdatedVersion = isFinalorOutdatedVersion(globalState);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [contactPersonToDeleteUuid, setContactPersonToDeleteUuid] =
    useState<string>();

  const markedAnswerAsSigned = true;
  const userDetails =
    Array.isArray(question.data.userValue) && question.data.userValue.length > 0
      ? (
          question.data.userValue as CoreInterfaces.ContactPersonUserDetails[]
        ).map((userDetails: CoreInterfaces.ContactPersonUserDetails) => ({
          ContactPersonId: userDetails.ContactPersonId,
          PersonalNumber: userDetails.PersonalNumber,
          IsSigned: userDetails.IsSigned,
          Uuid: userDetails.Uuid,
          SigningMethod:
            userDetails.SigningMethod || Constants.SigningMethod.BankIDSE,
        }))
      : [createEmptyContactPerson(markedAnswerAsSigned)];

  // Identity must stay stable across renders: MUI compares `value` by reference to
  // decide whether to reset the input, and a fresh object wipes what the user typed.
  const options: CoreInterfaces.SelectableKeyValueItem[] = useMemo(() => {
    const selectedIds = new Set(
      (Array.isArray(question.data.userValue)
        ? (question.data.userValue as CoreInterfaces.ContactPersonUserDetails[])
        : []
      ).map((detail) => detail.ContactPersonId)
    );
    return question.data.options.map((option) => ({
      ...option,
      isSelected: selectedIds.has(option.key),
    }));
  }, [question.data.options, question.data.userValue]);

  const addContactPersons = () => {
    if (userDetails.length < 10 && !isFinalOrOutdatedVersion) {
      dispatch({
        type: Constants.AppStateActions.AddContactPerson,
      });
    }
  };

  const deleteContactPerson = () => {
    dispatch({
      type: Constants.AppStateActions.DeleteContactPerson,
      payload: {
        contactPersonToDeleteUuid,
      },
    });
  };

  const deleteDialogDTO: DTOs.GtDialogDTO = {
    data: {
      title: t("General.DeleteContactPerson"),
      maxWidth: "xs",
      leftButtons: [],
      rightButtons: [
        <Button
          onClick={() => setIsModalVisible(!isModalVisible)}
          color="inherit"
          variant="contained"
          key="cancelAction"
        >
          {t("General.Cancel")}
        </Button>,
        <Button
          onClick={() => {
            deleteContactPerson();
            setIsModalVisible(!isModalVisible);
          }}
          color="tertiary"
          variant="contained"
          key="delecteLicense"
        >
          {t("General.DeleteContactPerson")}
        </Button>,
      ],
    },
    api: {
      onClose: () => setIsModalVisible(!isModalVisible),
    },
    state: {
      isOpen: isModalVisible,
      isFullWidth: true,
    },
  };

  const updateContactPersonProperty = ({
    value,
    contactPersonUuid,
    property,
  }: {
    value: string | boolean;
    contactPersonUuid: string;
    property: keyof CoreInterfaces.ContactPersonUserDetails;
  }): void => {
    dispatch({
      type: Constants.AppStateActions.UpdateContactPersonValue,
      payload: {
        contactPersonUuid,
        property,
        value,
      },
    });
  };

  return (
    <>
      {isModalVisible && (
        <GtDialog gtDialogDTO={deleteDialogDTO}>
          <article> {t("General.DeleteContactPerson")}</article>
        </GtDialog>
      )}
      <FormGroup className="gt-multipleEntriesInputContainer">
        {userDetails.map((contactPerson, index) => (
          <div
            key={contactPerson.Uuid}
            className="gt-multipleEntriesInputContainer__rows"
          >
            <Autocomplete
              data-testid={question.data.code}
              className="gt-Autocomplete"
              disablePortal
              disabled={isDisabled}
              options={options}
              getOptionLabel={(option) => option.value ?? ""}
              getOptionKey={(option) => option.key}
              filterOptions={contactFilterOptions}
              getOptionDisabled={(option) => option.isSelected === true}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              value={
                options.find(
                  (option) => option.key === contactPerson.ContactPersonId
                ) || null
              }
              onChange={(_, evt) => {
                const selectedKey = evt?.key;
                updateContactPersonProperty({
                  value: selectedKey,
                  contactPersonUuid: contactPerson.Uuid,
                  property: "ContactPersonId",
                });
              }}
              renderOption={(props, option) => {
                const { key, ...restProps } = props;
                return (
                  <Box
                    className="gt-multipleEntriesInputContainer__rows"
                    component="li"
                    key={key}
                    {...restProps}
                    data-testid={`option_question${question.data.code}_option${option.key}`}
                  >
                    {option.value}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("Options.PleaseSelect")}
                />
              )}
            />
            <GtDebouncedInput
              value={contactPerson.PersonalNumber ?? ""}
              className="gt-multipleEntriesInputContainer__rows-row gt-TextField"
              onChange={(sanitizedValue: string) =>
                updateContactPersonProperty({
                  value: sanitizedValue,
                  contactPersonUuid: contactPerson.Uuid,
                  property: "PersonalNumber",
                })
              }
              placeholder={t("Options.PersonalNumber")}
              updateTimestamp={lastVersionChangedDate}
              sanitizationFn={formatStringToPersonalNumber}
              disabled={isFinalOrOutdatedVersion}
            />
            {}
            <div className="gt-digitalSigningContainer">
              <Tooltip title={t("General.DigitalSigningTooltip")}>
                <IconButton
                  size="small"
                  disabled={isDisabled || isFinalOrOutdatedVersion}
                  className="gt-digitalSigningContainer__tooltip"
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Autocomplete
                className="gt-Autocomplete gt-multipleEntriesInputContainer__rows-row gt-digitalSigningContainer__dropdown"
                disablePortal
                disabled={isDisabled || isFinalOrOutdatedVersion}
                options={[
                  {
                    key: Constants.SigningMethod.BankIDSE,
                    value: t("Options.SigningMethod.bankid-se"),
                  },
                  {
                    key: Constants.SigningMethod.BankIDNO,
                    value: t("Options.SigningMethod.bankid-no"),
                  },
                  {
                    key: Constants.SigningMethod.EIDFI,
                    value: t("Options.SigningMethod.eID-fi"),
                  },
                  {
                    key: Constants.SigningMethod.EIDDK,
                    value: t("Options.SigningMethod.eID-dk"),
                  },
                  {
                    key: Constants.SigningMethod.TouchSign,
                    value: t("Options.SigningMethod.touch-sign"),
                  },
                ]}
                getOptionLabel={(option) => option.value}
                value={
                  contactPerson.SigningMethod
                    ? [
                        {
                          key: Constants.SigningMethod.BankIDSE,
                          value: t("Options.SigningMethod.bankid-se"),
                        },
                        {
                          key: Constants.SigningMethod.BankIDNO,
                          value: t("Options.SigningMethod.bankid-no"),
                        },
                        {
                          key: Constants.SigningMethod.EIDFI,
                          value: t("Options.SigningMethod.eID-fi"),
                        },
                        {
                          key: Constants.SigningMethod.EIDDK,
                          value: t("Options.SigningMethod.eID-dk"),
                        },
                        {
                          key: Constants.SigningMethod.TouchSign,
                          value: t("Options.SigningMethod.touch-sign"),
                        },
                      ].find(
                        (option) => option.key === contactPerson.SigningMethod
                      ) || null
                    : {
                        key: Constants.SigningMethod.BankIDSE,
                        value: t("Options.BankIDSE"),
                      }
                }
                onChange={(_, selectedOption) => {
                  updateContactPersonProperty({
                    value:
                      selectedOption?.key || Constants.SigningMethod.BankIDSE,
                    contactPersonUuid: contactPerson.Uuid,
                    property: "SigningMethod",
                  });
                }}
                renderOption={(props, option) => (
                  <Box
                    className="gt-multipleEntriesInputContainer__rows"
                    component="li"
                    {...props}
                  >
                    {option.value}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("Options.DigitalSigningMethod")}
                  />
                )}
              />
            </div>
            <div className="gt-multipleEntriesInputContainer__rows-row-checkbox">
              <div>
                <Checkbox
                  size="medium"
                  checked={contactPerson.IsSigned}
                  onChange={(e: any) => {
                    updateContactPersonProperty({
                      value: e.target.checked,
                      contactPersonUuid: contactPerson.Uuid,
                      property: "IsSigned",
                    });
                  }}
                  disabled={
                    (userDetails.length === 1 && index === 0) ||
                    isFinalOrOutdatedVersion
                  }
                />
                {index === 0 ? t("Options.Signed") : t("Options.SignedOwner")}
              </div>
              {(index > 0 || (index === 0 && userDetails.length > 1)) &&
                !isFinalOrOutdatedVersion && (
                  <Tooltip
                    title={t(`Forms.Button.${question.data.code}.Remove`)}
                    placement="right"
                  >
                    <Button
                      color="tertiary"
                      className="gt-multipleEntriesFormGroupButton gt-multipleEntriesFormGroupButton--delete"
                      onClick={() => {
                        setIsModalVisible(!isModalVisible);
                        setContactPersonToDeleteUuid(contactPerson.Uuid);
                      }}
                    >
                      <PersonRemoveIcon />
                    </Button>
                  </Tooltip>
                )}
            </div>
          </div>
        ))}
        <Tooltip
          title={t(`Forms.Button.${question.data.code}.Add`)}
          placement="left"
        >
          <Button
            className={`gt-multipleEntriesInputContainerButton 
          ${
            userDetails.length <= 9 && !isFinalOrOutdatedVersion
              ? " gt-multipleEntriesInputContainerButton--add"
              : " gt-multipleEntriesInputContainerButton--disabled"
          }
        }`}
            onClick={addContactPersons}
          >
            <PersonAddAlt1Icon />
          </Button>
        </Tooltip>
      </FormGroup>
    </>
  );
}

export default QuestionContactPersonUserDetails;
